/** @jsxImportSource @emotion/react */
"use client";
import type { Interpolation } from "@emotion/react";
import { useRouter } from "@i18n/next";
import { HallowAnalyticsEvent, useAnalytics } from "@packages/analytics";
import type { BibleLanguage, BibleTranslations } from "@packages/sdk";
import { useLocalStorageState } from "@packages/sdk";
import {
  Button,
  ChevronDownIcon,
  ModalDropdownMenu,
  ModalDropdownMenuItem,
  ScrollArea,
  spacing,
  Text,
} from "@packages/ui";
import { useParams } from "next/navigation";
import { Fragment, useCallback, useContext } from "react";

import { BibleLanguageContext } from "../BibleLanguageProvider";

const dropdownStyle: Interpolation = {
  display: "flex",
  flexDirection: "column",
  flexWrap: "nowrap",
  rowGap: spacing.xs,
};
const translationBoxStyle: Interpolation = {
  padding: spacing.m,
};
const rtlBoxStyle: Interpolation = {
  flexDirection: "column-reverse",
};
const rtlItemStyle: Interpolation = {
  flexDirection: "row-reverse",
};
const languageHeaderStyle: Interpolation = {
  margin: `calc(${spacing.m} - ${spacing.xs}) 0`,
};
const bigButtonStyle: Interpolation = {
  height: "auto",
};

const BibleLanguages: Record<BibleLanguage, string> = {
  en: "English",
  ar: "اَلْعَرَبِيَّةُ",
  de: "Deutsch",
  es: "Español",
  fr: "Français",
  hi: "हिन्दी",
  it: "Italiano",
  pl: "Polski",
  pt: "Português",
  tl: "Tagalog",
};

type TranslationSelectorProps = {
  translations: BibleTranslations["translations"];
  className?: string;
};

export const TranslationSelector = ({
  translations,
  className = "",
}: TranslationSelectorProps) => {
  const analytics = useAnalytics();
  const { rtl } = useContext(BibleLanguageContext);
  const { translation, book, chapterVerse } = useParams();
  const router = useRouter();
  const [, setTranslation] = useLocalStorageState<string>({
    key: "bibleTranslation",
    defaultValue: Array.isArray(translation) ? translation[0] : translation,
  });

  const gotoTranslation = useCallback(
    (tname: string) => {
      analytics.track({
        event: HallowAnalyticsEvent.SelectedBibleTranslation,
        properties: {
          bible_translation_name: tname,
          bible_translation_id:
            translations?.find((t) => t.name === tname)?.id ?? null,
        },
      });
      setTranslation(tname);
      return router.push(
        `/bible/${tname}/${book}/${chapterVerse[0]}${chapterVerse[1] ? `/${chapterVerse[1]}` : ""}`,
      );
    },
    [book, chapterVerse],
  );

  const translationList = useCallback(
    (t: BibleTranslations["translations"][0]) => (
      <ModalDropdownMenuItem
        key={`option_${t.name}`}
        onSelect={gotoTranslation.bind(this, t.name)}
        selected={translation === t.name}
        dropdownMenuItemCSS={[bigButtonStyle, rtl ? rtlItemStyle : null]}
        modalItemCSS={[bigButtonStyle, rtl ? rtlItemStyle : null]}
      >
        <>
          {/* Apparently we have to have a bonus wrapper if we want a wrapper */}
          <div>
            <Text as={"div"} type={"title"} size={"s"}>
              {t.name}
            </Text>
            <Text as={"div"} type={"detail"} size={"xl"}>
              {t.description}
            </Text>
          </div>
        </>
      </ModalDropdownMenuItem>
    ),
    [rtl, translation],
  );

  const languageList = useCallback(
    ([code, name]) => {
      const relevantTranslations = translations.filter(
        (t) => t.language === code,
      );
      if (relevantTranslations.length) {
        return (
          <Fragment key={`translationSet_${code}`}>
            <Text
              key={`${code}_header`}
              as={"h2"}
              type={"title"}
              size={"m"}
              css={languageHeaderStyle}
            >
              {name}
            </Text>
            {relevantTranslations.map(translationList)}
          </Fragment>
        );
      }
      return null;
    },
    [translations, translationList],
  );

  return (
    <ModalDropdownMenu
      contentCSS={translationBoxStyle}
      trigger={
        <Button
          variant={"outline"}
          onClickCapture={() => {
            analytics.track({
              event: HallowAnalyticsEvent.TappedBibleTranslations,
            });
          }}
          endIcon={<ChevronDownIcon />}
        >
          {translation}
        </Button>
      }
    >
      <ScrollArea viewportProps={{ className }}>
        <div css={[dropdownStyle, rtl ? rtlBoxStyle : null]}>
          {Object.entries(BibleLanguages).map(languageList)}
        </div>
      </ScrollArea>
    </ModalDropdownMenu>
  );
};
